import { getFunctions, httpsCallable } from "firebase/functions";

const functions = getFunctions();

const callLLMAPICallable = httpsCallable(functions, "callLLMAPI", {
  timeout: 300000,
});

/**
 * The composed system prompt is never sent back from the server and never logged here. It
 * carries the org's prompt IP and the patient's allergies and medications verbatim, so an
 * excerpt in a console is PHI sitting in a browser. To read a prompt, read it in the Prompts
 * manager; to confirm the server injected the record, run
 * scripts/verifyPatientRecordInPrompts.sh.
 *
 * What IS printed, on localhost only, is the patient record block this app is sending — the
 * thing that silently went out empty and cost a client demo. It is the outgoing payload, so
 * no round trip and no server involvement is needed, and the hostname check means it cannot
 * fire for a real user on a real deployment.
 */
const isLocalhost = () => {
  try {
    return ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);
  } catch {
    return false;
  }
};

/**
 * Current ChartMind encounter, set once by ChartMindSessionProvider and attached to every
 * call below. Module-level on purpose: the alternative is threading a sessionId parameter
 * through every hook that calls invokeLLMPrompt (diagnosis, plan, treatment, discharge,
 * chart, peer view), and every one of those is a place to forget it.
 *
 * Not PHI — it's the Firestore session doc id, which the URL already exposes. RAM only,
 * same as the session it names.
 */
let currentEncounterId = null;
let currentEncounterIsSimulation = false;
let currentEncounterFeature = "chartmind";

/**
 * Prompt-id prefix each feature's encounter owns.
 *
 * The provider that sets the id lives above the route boundary, so it stays mounted while the
 * clinician is anywhere in the dashboard. Without this guard an Ask Me Anything question or a
 * Prompts-manager test run picked up the ambient ChartMind encounter id, and because the
 * rollup stores one feature label per encounter, the last call written decided what that
 * encounter was — quietly moving a real encounter out of the ChartMind cost distribution and
 * adding unrelated spend to it.
 */
const ENCOUNTER_PROMPT_PREFIXES = {
  chartmind: "chartmind-",
};

export const setLLMEncounterId = (
  sessionId,
  { simulation = false, feature = "chartmind" } = {},
) => {
  currentEncounterId = typeof sessionId === "string" && sessionId.trim() ? sessionId : null;
  currentEncounterIsSimulation = Boolean(simulation);
  currentEncounterFeature = feature;
};
/** callLLMAPI's error when a prompt id has no content on this environment. */
const MISSING_PROMPT = /No prompt content is available/i;

/**
 * Call a prompt, and if it has not been created on this environment yet, call
 * `fallbackPromptId` instead (with `fallbackUserMessage` when given). Inpatient prompts use this
 * so an environment that has not been seeded runs the base prompt rather than failing the step.
 * Any other error is thrown as it is.
 *
 * `focus` (ChartMind's specialty and setting focus) goes with either call; callLLMAPI adds it to
 * the system prompt.
 */
export async function invokeLLMPrompt({ fallbackPromptId, fallbackUserMessage, ...request }) {
  try {
    return await invokeLLMPromptOnce(request);
  } catch (err) {
    if (!fallbackPromptId || !MISSING_PROMPT.test(err?.message || "")) throw err;
    console.warn(
      `[callLLMAPI] ${request.promptId} is not set up on this environment; using ${fallbackPromptId}. ` +
        "Seed it with scripts/seedInpatientPrompts.js.",
    );
    return invokeLLMPromptOnce({
      ...request,
      promptId: fallbackPromptId,
      userMessage: fallbackUserMessage ?? request.userMessage,
    });
  }
}

async function invokeLLMPromptOnce({
  promptId,
  userMessage,
  config,
  systemPrompt,
  organizationId,
  regionId,
  preview,
  includeEmbeddedDocuments,
  embeddedDocumentsOptions,
  includePatientDocuments,
  patientDocumentsOptions,
  patientContext,
  focus,
}) {
  const payload = {
    promptId,
    userMessage,
  };

  if (config && typeof config === "object") payload.config = config;
  if (typeof systemPrompt === "string" && systemPrompt.trim()) {
    payload.systemPrompt = systemPrompt;
  }
  if (typeof organizationId === "string" && organizationId.trim()) {
    payload.organizationId = organizationId;
  }
  if (typeof regionId === "string" && regionId.trim()) {
    payload.regionId = regionId;
  }
  if (preview && typeof preview === "object") {
    payload.preview = preview;
  }
  if (includeEmbeddedDocuments === true) {
    payload.includeEmbeddedDocuments = true;
  }
  if (embeddedDocumentsOptions && typeof embeddedDocumentsOptions === "object") {
    payload.embeddedDocumentsOptions = embeddedDocumentsOptions;
  }
  if (includePatientDocuments === true) {
    payload.includePatientDocuments = true;
  }
  if (patientDocumentsOptions && typeof patientDocumentsOptions === "object") {
    payload.patientDocumentsOptions = patientDocumentsOptions;
  }
  // Built by utils/patientContext. callLLMAPI renders `record` into the system prompt as
  // authoritative and appends the hardcoded precedence rule; `selfReport` stays labelled
  // as patient-reported.
  if (patientContext && typeof patientContext === "object") {
    payload.patientContext = patientContext;
  }
  if (typeof focus === "string" && focus.trim()) payload.focus = focus;

  // A `preview` layer only ever comes from the Prompts manager's Try Prompt, so it is prompt
  // development by definition — real spend, but not a patient encounter. Flagged rather than
  // tagged: it must never land in a per-encounter figure even when the prompt under test is a
  // ChartMind one and a real encounter happens to be open in another tab.
  const isPromptDevelopment = Boolean(payload.preview);
  if (isPromptDevelopment) payload.isSimulation = true;

  // Cost attribution only — lets the Usage Monitor group a run of calls into one encounter.
  const ownsPrompt =
    typeof promptId === "string" &&
    promptId.startsWith(ENCOUNTER_PROMPT_PREFIXES[currentEncounterFeature] || "\0");
  if (currentEncounterId && ownsPrompt && !isPromptDevelopment) {
    payload.sessionId = currentEncounterId;
    // Admin prompt-development runs are real spend but not real clinical usage. Flagged so
    // cost-per-encounter can exclude them; in prod today they are most of the ChartMind
    // traffic, which is exactly how a per-patient figure ends up being mostly testing.
    if (currentEncounterIsSimulation) payload.isSimulation = true;
  }

  const startedAt = performance.now();
  console.log("[callLLMAPI] →", {
    promptId,
    userMessageLength: typeof userMessage === "string" ? userMessage.length : 0,
    organizationId: payload.organizationId || null,
    hasSystemPrompt: !!payload.systemPrompt,
    hasPreview: !!payload.preview,
    // Lengths only, never the content. The whole bug class here is a step quietly
    // running without the record, so make it visible on every call.
    patientRecordChars: payload.patientContext?.record?.length || 0,
    patientSelfReportChars: payload.patientContext?.selfReport?.length || 0,
  });

  // Localhost only. Prints per step, so walking an encounter with the console open shows the
  // record arriving at each one, or shows the gap.
  if (isLocalhost() && payload.patientContext) {
    console.log(
      `[callLLMAPI] patient record sent to ${promptId}:\n` +
      `${payload.patientContext.record || "(none)"}\n` +
      (payload.patientContext.selfReport
        ? `--- patient self-report (not authoritative) ---\n${payload.patientContext.selfReport}\n`
        : ""),
    );
  } else if (isLocalhost()) {
    console.warn(`[callLLMAPI] NO patient record sent to ${promptId}`);
  }

  let result;
  try {
    result = await callLLMAPICallable(payload);
  } catch (err) {
    const ms = Math.round(performance.now() - startedAt);
    console.error("[callLLMAPI] ✗", { promptId, ms, error: err?.message || err });
    throw err;
  }

  const ms = Math.round(performance.now() - startedAt);
  const output = result?.data?.output;

  const meta = result?.data?.meta || null;
  console.log("[callLLMAPI] ←", {
    promptId,
    ms,
    outputLength: typeof output === "string" ? output.length : 0,
    hasMeta: !!meta,
    systemPromptLength: meta?.systemPromptLength ?? null,
    systemPromptSectionCount: meta?.systemPromptSectionCount ?? null,
    patientRecordApplied: meta?.patientRecordApplied ?? null,
    temperature: meta?.temperature ?? null,
    model: meta?.model ?? null,
  });
  if (!output || typeof output !== "string") {
    throw new Error("callLLMAPI returned no output");
  }

  return {
    output,
    meta: result?.data?.meta || null,
    embeddedDocuments: result?.data?.meta?.embeddedDocuments || null,
    raw: result?.data || null,
  };
}

